import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, ArrowLeft, Eye, Save } from "lucide-react";
import type { BlogPost, InsertBlogPost } from "@shared/schema";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export default function Admin() {
  const [view, setView] = useState<"list" | "edit">("list");
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    coverImage: "",
    published: false,
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/posts"],
    queryFn: async () => {
      const res = await fetch("/api/posts", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertBlogPost) => {
      const res = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create post");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts"] });
      toast({ title: "Post created successfully" });
      resetForm();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertBlogPost> }) => {
      const res = await fetch(`/api/posts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to update post");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts"] });
      toast({ title: "Post updated successfully" });
      resetForm();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/posts/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete post");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts"] });
      toast({ title: "Post deleted" });
    },
  });

  function resetForm() {
    setForm({ title: "", slug: "", excerpt: "", content: "", coverImage: "", published: false });
    setEditingPost(null);
    setView("list");
  }

  function startEdit(post: BlogPost) {
    setEditingPost(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      coverImage: post.coverImage || "",
      published: post.published,
    });
    setView("edit");
  }

  function startNew() {
    setEditingPost(null);
    setForm({ title: "", slug: "", excerpt: "", content: "", coverImage: "", published: false });
    setView("edit");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const slug = form.slug || slugify(form.title);
    const data = { ...form, slug };

    if (editingPost) {
      updateMutation.mutate({ id: editingPost.id, data });
    } else {
      createMutation.mutate(data as InsertBlogPost);
    }
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          {view === "list" ? (
            <>
              <div className="flex items-center justify-between mb-12">
                <div>
                  <h1 className="text-3xl font-display font-bold mb-2">Blog Admin</h1>
                  <p className="text-muted-foreground">Create, edit, and manage your blog posts.</p>
                </div>
                <Button onClick={startNew} data-testid="button-new-post">
                  <Plus size={18} className="mr-2" /> New Post
                </Button>
              </div>

              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-card border border-white/5 rounded-xl p-6 animate-pulse">
                      <div className="h-6 bg-white/5 rounded w-1/3" />
                    </div>
                  ))}
                </div>
              ) : posts && posts.length > 0 ? (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card
                      key={post.id}
                      data-testid={`card-admin-post-${post.id}`}
                      className="border-white/5 p-6 flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold truncate">{post.title}</h3>
                          <Badge variant={post.published ? "default" : "secondary"} className={post.published ? "" : "bg-white/5"}>
                            {post.published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{post.excerpt}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        {post.published && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer">
                              <Eye size={16} />
                            </a>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => startEdit(post)} data-testid={`button-edit-${post.id}`}>
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => {
                            if (confirm("Are you sure you want to delete this post?")) {
                              deleteMutation.mutate(post.id);
                            }
                          }}
                          data-testid={`button-delete-${post.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                  <h3 className="text-xl font-display font-semibold mb-3">No posts yet</h3>
                  <p className="text-muted-foreground mb-6">Create your first blog post to get started.</p>
                  <Button onClick={startNew}>
                    <Plus size={18} className="mr-2" /> Create First Post
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" className="mb-8 text-muted-foreground -ml-4" onClick={resetForm}>
                <ArrowLeft size={16} className="mr-2" /> Back to Posts
              </Button>

              <h1 className="text-3xl font-display font-bold mb-8">
                {editingPost ? "Edit Post" : "New Post"}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    data-testid="input-title"
                    placeholder="Your post title"
                    value={form.title}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        title: e.target.value,
                        slug: editingPost ? form.slug : slugify(e.target.value),
                      });
                    }}
                    className="bg-card border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    data-testid="input-slug"
                    placeholder="url-friendly-slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="bg-card border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="excerpt">Excerpt</Label>
                  <Textarea
                    id="excerpt"
                    data-testid="input-excerpt"
                    placeholder="A short summary of the post..."
                    rows={3}
                    value={form.excerpt}
                    onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                    className="bg-card border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content (HTML)</Label>
                  <Textarea
                    id="content"
                    data-testid="input-content"
                    placeholder="<p>Write your post content in HTML...</p>"
                    rows={16}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="bg-card border-white/10 font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coverImage">Cover Image URL (optional)</Label>
                  <Input
                    id="coverImage"
                    data-testid="input-cover-image"
                    placeholder="https://example.com/image.jpg"
                    value={form.coverImage}
                    onChange={(e) => setForm({ ...form, coverImage: e.target.value })}
                    className="bg-card border-white/10"
                  />
                </div>

                <div className="flex items-center gap-3 py-4">
                  <Switch
                    id="published"
                    data-testid="switch-published"
                    checked={form.published}
                    onCheckedChange={(checked) => setForm({ ...form, published: checked })}
                  />
                  <Label htmlFor="published">Publish immediately</Label>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    data-testid="button-save-post"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="min-w-[140px]"
                  >
                    <Save size={16} className="mr-2" />
                    {createMutation.isPending || updateMutation.isPending
                      ? "Saving..."
                      : editingPost
                        ? "Update Post"
                        : "Create Post"}
                  </Button>
                  <Button type="button" variant="outline" className="border-white/10" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </>
  );
}
